import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { uniq, uniqBy } from 'lodash';
import moment from 'moment';

import { reset, Field } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import RootRef from '@material-ui/core/RootRef';

import CheckboxField from 'components/fields/CheckboxField';
import Badge from 'components/common/Badge';
import IconButton from 'components/common/IconButton';
import IconMoreVert from 'components/icons/IconMoreVert';
import IconBlurb from 'components/icons/IconBlurb';
import IconBlurbs from 'components/icons/IconBlurbs';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';
import IconDocument from 'components/icons/IconDocument';
import IconThumbUp from 'components/icons/IconThumbUp';
import Tooltip from 'components/common/Tooltip';
import Menu, { MenuItem } from 'components/common/Menu';
import Link from 'components/common/Link';
import Chip from 'components/common/Chip';
import DeleteMenu from 'components/common/DeleteMenu';
import InsuranceViewDialog from 'components/patient/InsuranceViewDialog';
import InsuranceCreateEditDialog from 'components/patient/InsuranceCreateEditDialog';
import OrderNotes from 'components/workflow/OrderNotes';
import PrintDialog from 'components/common/PrintDialog';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';
import InventoryStockLineHistoryViewDialog from 'components/inventory/InventoryStockLineHistoryViewDialog';
import ClaimLogViewDialog from 'components/workflow/ClaimLogViewDialog';
import IconWatch from 'components/icons/IconWatch';
import Watchers from 'components/patient/Watchers';
import EventLogViewDialog from 'components/patient/EventLogViewDialog';
import NotificationsDialog from 'components/patient/NotificationsDialog';
import DocumentsViewDialog from 'components/common/DocumentsViewDialog';

import { bulkUpdateOrders, fetchOrder } from 'redux/modules/orders';
import { fetchArchiveOrder } from 'redux/modules/archiveOrders';
import { archiveOrder, unarchiveOrder, deleteArchiveOrder } from 'redux/modules/archiveOrders';

import dateFormat from 'config/dateFormat';
import timeDateFormat from 'config/timeDateFormat';

import formatPrice from 'utils/formatPrice';
import formatPrescriberName from 'utils/formatPrescriberName';
import objectDiff from 'utils/objectDiff';
import splitLongWords from 'utils/splitLongWords';
import firstCharToUpperCase from 'utils/firstCharToUpperCase';
import isDescendant from 'utils/isDescendant';
import formatOrderNumber from 'utils/formatOrderNumber';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';

import pxToRem from 'styles/pxToRem';
import hexToRgb from 'styles/hexToRgb';

import OrdersTableRowItem from './OrdersTableRowItem';
import OrdersTableRowSalesRepresentatives from './OrdersTableRowSalesRepresentatives';
import OrdersTableRowInsurances from './OrdersTableRowInsurances';

const styles = theme => ({
  tableRow: {
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
  expansionPanelClickable: {
  },
  cellExpansionActions: {
    position: 'relative',
    borderRight: `1px solid ${theme.palette.primary.A100}`,
    width: 60,
    padding: 0,
    '&:first-child': {
      paddingLeft: 32,
    },
  },
  cellPatient: {
    width: '16%',
  },
  cellPrescriber: {
    width: '12%',
  },
  cellInsurance: {
    width: '16%',
  },
  cellExpansionActionsNotification: {
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: theme.palette.validation[500],
    },
  },
  cellSpecialState: {
    textTransform: 'capitalize',
    padding: '0px 8px 0px 8px',
    width: 16,
  },
  rowFullWidth: {
    height: 'auto',
  },
  cellFullWidth: {
    padding: 0,
    border: 'none',
    '&:last-child': {
      padding: 0,
    },
  },
  cellButton: {
    width: 44,
    textAlign: 'right',
    padding: 0,
    '&:last-child': {
      padding: 0,
    },
  },
  cellTransmission: {
    width: 24,
    padding: 0,
  },
  cellDateOfService: {
    width: 105,
  },
  cellHCPCSCode: {
    width: '10.25%',
  },
  cellHCPCSCodeCollection: {
    width: '8%',
  },
  cellTotalReimbs: {
    width: 74,
  },
  cellTotalCharge: {
    width: 74,
  },
  cellSales: {
    width: '12%',
  },
  cellSalesVariantCombined: {
    width: '18%',
  },
  cellDateOfDelivery: {
    width: 105,
    textAlign: 'center',
  },
  cellCreationDate: {
    width: 40,
    textAlign: 'center',
  },
  cellFacility: {
    width: 20,
    textAlign: 'center',
  },
  chip: {
    marginLeft: 8,
    verticalAlign: 'middle',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  buttonExpand: {
    margin: -12,
  },
  iconBlurb: {
    width: 18,
    height: 17,
  },
  iconMoreVert: {
    width: 24,
    height: 24,
  },
  iconArrowHeadBottom: {
    width: 12,
    height: 8,
  },
  iconArrowHeadBottomExpanded: {
    transform: 'rotate(180deg)',
  },
  details: {
    display: 'block',
    padding: 0,
    borderBottom: `1px solid ${theme.palette.primary.A400}`,
    background: theme.palette.grey[100],
  },
  states: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: 26,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  state: {
    width: 12,
    height: 12,
    fontSize: 10,
    margin: 1,
    textAlign: 'center',
    verticalAlign: 'middle',
    color: theme.palette.common.white,
    borderRadius: '50%',
  },
  stateIntake: {
    backgroundColor: theme.palette.states.intake,
  },
  stateDelivery: {
    backgroundColor: theme.palette.states.delivery,
  },
  stateBilling: {
    backgroundColor: theme.palette.states.billing,
  },
  stateCollection: {
    backgroundColor: theme.palette.states.collection,
  },
  statePaid: {
    backgroundColor: theme.palette.states.paid,
  },
  statesLong: {
    '& $statePaid': {
      position: 'absolute',
      bottom: 0,
      left: 20,
    },
  },
  typographyNotes: {
    position: 'absolute',
    left: 38,
    top: '50%',
    marginTop: -8,
    color: theme.palette.primary[500],
  },
  typographyOrderLinesCount: {
    display: 'inline-block',
    fontSize: pxToRem(13),
    width: 10,
  },
  link: {
    wordBreak: 'break-word',
  },
  dateOfService: {
    display: 'inline-block',
    maxWidth: 80,
  },
  buttonNotesTooltipOpen: {
    position: 'relative',
    backgroundColor: `rgba(${hexToRgb(theme.palette.common.black)}, 0.08)`,
    '&:before': {
      content: '""',
      position: 'absolute',
      top: -20,
      right: 40,
      bottom: -20,
      left: -20,
      backgroundColor: 'transparent',
    },
  },
  buttonNotesWithNotes: {
    color: theme.palette.primary[500],
  },
  noteTooltipPopper: {
    zIndex: 1290,
    marginBottom: 30,
  },
  noteTooltipPopperLast: {
    marginBottom: 0,
  },
  badgeInsurance: {
    marginRight: 4,
  },
  statusIcon: {
    display: 'inline-block',
    width: 10,
    height: 10,
    marginRight: 4,
    borderRadius: '50%',
    overflow: 'hidden',
    textIndent: '100%',
    cursor: 'pointer',
  },
  statusIconActive: {
    backgroundColor: theme.palette.active[500],
  },
  statusIconInactive: {
    backgroundColor: theme.palette.validation[500],
  },
  orderSelection: {
    position: 'relative',
    top: -2,
    display: 'inline-block',
    margin: '0 -5px 0 -8px',
  },
  tooltipTransmission: {
    position: 'relative',
    backgroundColor: theme.palette.grey[600],
    borderRadius: '50%',
  },
  typographyTransmission: {
    position: 'relative',
    zIndex: 2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: '50%',
    color: theme.palette.common.white,
    cursor: 'pointer',
  },
  typographyTransmissionApproved: {
    backgroundColor: theme.palette.add['500'],
  },
  typographyTransmissionCreated: {
    backgroundColor: theme.palette.validation['300'],
  },
  typographyTransmissionSend: {
    backgroundColor: theme.palette.notification['500'],
  },
  typographyTransmissionRejected: {
    backgroundColor: theme.palette.validation['500'],
  },
  createdFourth: {
    display: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: theme.palette.validation['300'],
    width: 12,
    height: 12,
    borderTopLeftRadius: 12,
    zIndex: 1,
  },
  sendFourth: {
    display: 'none',
    position: 'absolute',
    bottom: 0,
    left: 0,
    backgroundColor: theme.palette.notification['500'],
    width: 12,
    height: 12,
    borderBottomLeftRadius: 12,
    zIndex: 1,
  },
  approvedFourth: {
    display: 'none',
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: theme.palette.add['500'],
    width: 12,
    height: 12,
    borderTopRightRadius: 12,
    zIndex: 1,
  },
  rejectedFourth: {
    display: 'none',
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: theme.palette.validation['500'],
    width: 12,
    height: 12,
    borderBottomRightRadius: 12,
    zIndex: 1,
  },
  createdHalf: {
    display: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: theme.palette.validation['300'],
    width: 12,
    height: 24,
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    zIndex: 1,
  },
  sendHalf: {
    display: 'none',
    position: 'absolute',
    bottom: 0,
    left: 0,
    backgroundColor: theme.palette.notification['500'],
    width: 12,
    height: 24,
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    zIndex: 1,
  },
  approvedHalf: {
    display: 'none',
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: theme.palette.add['500'],
    width: 12,
    height: 24,
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    zIndex: 1,
  },
  rejectedHalf: {
    display: 'none',
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: theme.palette.validation['500'],
    width: 12,
    height: 24,
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    zIndex: 1,
  },
  tooltipTransmissionСreatedUnsend: {
    '& $createdHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionSendUnsend: {
    '& $sendHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionApprovedUnsend: {
    '& $approvedHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionRejectedUnsend: {
    '& $rejectedHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionApprovedСreated: {
    '& $createdHalf': {
      display: 'block',
    },
    '& $approvedHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionRejectedСreated: {
    '& $createdHalf': {
      display: 'block',
    },
    '& $rejectedHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionСreatedSend: {
    '& $createdHalf': {
      display: 'block',
    },
    '& $sendHalf': {
      display: 'block',
      left: 'auto',
      right: 0,
      transform: 'rotate(180deg)',
    },
  },
  tooltipTransmissionApprovedSend: {
    '& $approvedHalf': {
      display: 'block',
    },
    '& $sendHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionRejectedSend: {
    '& $rejectedHalf': {
      display: 'block',
    },
    '& $sendHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionRejectedApproved: {
    '& $rejectedHalf': {
      display: 'block',
    },
    '& $approvedHalf': {
      display: 'block',
      left: 0,
      right: 'auto',
      transform: 'rotate(180deg)',
    },
  },
  tooltipTransmissionCreatedSendApprovedRejected: {
    '& $createdFourth': {
      display: 'block',
    },
    '& $sendFourth': {
      display: 'block',
    },
    '& $approvedFourth': {
      display: 'block',
    },
    '& $rejectedFourth': {
      display: 'block',
    },
  },
  tooltipTransmissionCreatedSendApproved: {
    '& $createdFourth': {
      display: 'block',
    },
    '& $sendFourth': {
      display: 'block',
    },
    '& $approvedHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionCreatedSendRejected: {
    '& $createdFourth': {
      display: 'block',
    },
    '& $sendFourth': {
      display: 'block',
    },
    '& $rejectedHalf': {
      display: 'block',
    },
  },
  tooltipTransmissionCreatedApprovedRejected: {
    '& $createdHalf': {
      display: 'block',
    },
    '& $approvedFourth': {
      display: 'block',
    },
    '& $rejectedFourth': {
      display: 'block',
    },
  },
  tooltipTransmissionSendApprovedRejected: {
    '& $sendHalf': {
      display: 'block',
    },
    '& $approvedFourth': {
      display: 'block',
    },
    '& $rejectedFourth': {
      display: 'block',
    },
  },
  tooltipTransmissionCreatedSendApprovedUnsend: {
    '& $createdFourth': {
      display: 'block',
    },
    '& $sendFourth': {
      display: 'block',
    },
    '& $approvedFourth': {
      display: 'block',
    },
  },
  tooltipTransmissionCreatedSendRejectedUnsend: {
    '& $createdFourth': {
      display: 'block',
    },
    '& $sendFourth': {
      display: 'block',
    },
    '& $rejectedFourth': {
      display: 'block',
    },
  },
  tooltipTransmissionCreatedApprovedRejectedUnsend: {
    '& $createdFourth': {
      display: 'block',
    },
    '& $approvedFourth': {
      display: 'block',
    },
    '& $rejectedFourth': {
      display: 'block',
    },
  },
  tooltipTransmissionSendApprovedRejectedUnsend: {
    '& $sendFourth': {
      display: 'block',
    },
    '& $approvedFourth': {
      display: 'block',
    },
    '& $rejectedFourth': {
      display: 'block',
    },
  },
  claimLogViewButton: {
    margin: -12,
  },
  patientDeleted: {
    color: theme.palette.validation[500],
  },
  iconWatch: {
    width: 24,
    height: 24,
  },
  iconButton: {
    margin: -4,
    color: theme.palette.grey[600],
  },
  iconButtonActive: {
    color: theme.palette.primary[500],
  },
  typographyCount: {
    width: 15,
  },
  typographyCountActive: {
    color: theme.palette.primary[500],
  },
  iconButtonMenu: {
    margin: -2,
    position: 'relative',
    left: -2,
  },
  tableRowArchive: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.grey[200])}, 0.7)`,
  },
  detailsArchive: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.grey[200])}, 0.7)`,
  },
  iconProrAuth: {
    display: 'inline-block',
    position: 'relative',
    verticalAlign: 'middle',
    marginLeft: 4,
  },
  iconDocument: {
    width: 20,
    height: 20,
    color: theme.palette.grey[500],
  },
  iconThumbUp: {
    position: 'absolute',
    left: -2,
    bottom: -2,
    width: 15,
    height: 15,
    color: theme.palette.primary[500],
  },
  facilityLogo: {
    width: 20,
    height: 'auto',
  },
  cellOrderNumber: {
    paddingLeft: 0,
  },
  iconSMS: {
    position: 'relative',
    width: 20,
    height: 20,
  },
  iconBlurbs: {
    width: 20,
    height: 20,
  },
  typographySMS: {
    position: 'absolute',
    left: 1,
    top: 4,
    fontSize: pxToRem(6),
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  typographyNewNotification: {
    width: 12,
    height: 12,
    position: 'absolute',
    top: -7,
    right: -4,
    borderRadius: 8,
    backgroundColor: theme.palette.notification[500],
    border: `1px solid ${theme.palette.common.white}`,
    fontSize: pxToRem(10),
    lineHeight: pxToRem(12),
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
});

class OrdersTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      notesTooltipHovered: false,
      notesTooltipClicked: false,
      notesTooltipDisabled: true,
      moreMenuOpen: false,
      deleteMenuOpen: false,
      expanded: false,
      insuranceViewDialogOpen: false,
      insuranceEditDialogOpen: false,
      printDialogOpen: false,
      signDialogOpen: false,
      printFormValues: null,
      tooltipsDisabled: true,
      stockLineHistoryProduct: null,
      claimLogViewDialogOpen: false,
      watcherMenuOpen: false,
      eventLogViewDialogOpen: false,
      notificationsDialogOpen: false,
      currentViewDocument: null,
      documentViewDialogOpen: false,
    };

    this.onMoreMenuButtonClick = this.onMoreMenuButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onDeleteMenuItemClick = this.onDeleteMenuItemClick.bind(this);
    this.onDeleteCancel = this.onDeleteCancel.bind(this);
    this.onDeleteSubmit = this.onDeleteSubmit.bind(this);
    this.onInsuranceViewButtonClick = this.onInsuranceViewButtonClick.bind(this);
    this.onInsuranceViewDialogClose = this.onInsuranceViewDialogClose.bind(this);
    this.onInsuranceEditButtonClick = this.onInsuranceEditButtonClick.bind(this);
    this.onInsuranceEditDialogClose = this.onInsuranceEditDialogClose.bind(this);
    this.onPrintMenuItemClick = this.onPrintMenuItemClick.bind(this);
    this.onPrintDialogClose = this.onPrintDialogClose.bind(this);
    this.onPrintDialogSign = this.onPrintDialogSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onTableRowClick = this.onTableRowClick.bind(this);
    this.onExpandButtonClick = this.onExpandButtonClick.bind(this);
    this.onTableRowMouseEnter = this.onTableRowMouseEnter.bind(this);
    this.onTableRowMouseLeave = this.onTableRowMouseLeave.bind(this);
    this.onNotesButtonMouseEnter = this.onNotesButtonMouseEnter.bind(this);
    this.onNotesTooltipMouseEnter = this.onNotesTooltipMouseEnter.bind(this);
    this.onNotesTooltipMouseLeave = this.onNotesTooltipMouseLeave.bind(this);
    this.onNotesButtonMouseLeave = this.onNotesButtonMouseLeave.bind(this);
    this.onOrderNotesSubmitSuccess = this.onOrderNotesSubmitSuccess.bind(this);
    this.onNotesButtonFocus = this.onNotesButtonFocus.bind(this);
    this.onNotesButtonBlur = this.onNotesButtonBlur.bind(this);
    this.onNotesButtonClick = this.onNotesButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onOrderNotesSubmitButtonBlur = this.onOrderNotesSubmitButtonBlur.bind(this);
    this.onButtonFocus = this.onButtonFocus.bind(this);
    this.onButtonBlur = this.onButtonBlur.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onStockLineHistoryViewLinkClick = this.onStockLineHistoryViewLinkClick.bind(this);
    this.onStockLineHistoryViewDialogClose = this.onStockLineHistoryViewDialogClose.bind(this);
    this.onClaimLogViewButtonClick = this.onClaimLogViewButtonClick.bind(this);
    this.onClaimLogViewDialogClose = this.onClaimLogViewDialogClose.bind(this);
    this.onWatchButtonClick = this.onWatchButtonClick.bind(this);
    this.onArchiveMenuItemClick = this.onArchiveMenuItemClick.bind(this);
    this.onUnarchiveMenuItemClick = this.onUnarchiveMenuItemClick.bind(this);
    this.onEventLogViewItemClick = this.onEventLogViewItemClick.bind(this);
    this.onEventLogViewDialogClose = this.onEventLogViewDialogClose.bind(this);
    this.focusOrderNoteInput = this.focusOrderNoteInput.bind(this);
    this.onNotificationsButtonClick = this.onNotificationsButtonClick.bind(this);
    this.onNotificationsDialogClose = this.onNotificationsDialogClose.bind(this);
    this.onDocumentView = this.onDocumentView.bind(this);
    this.onDocumentViewDialogClose = this.onDocumentViewDialogClose.bind(this);
  }

  focusOrderNoteInput() {
    if (this.createOrderNoteInputRef) {
      this.createOrderNoteInputRef.focus({ preventScroll: true });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const changedProps = objectDiff(this.props, nextProps);
    delete changedProps.change; // bugfix rdux-form change action creator
    // is sometimes undefined during load
    const changedState = objectDiff(this.state, nextState);
    return ((Object.keys(changedProps).length +
      Object.keys(changedState).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { notesTooltipDisabled, notesTooltipHovered } = this.state;
    if (notesTooltipDisabled && !prevState.notesTooltipDisabled) {
      window.addEventListener('scroll', this.onScroll);
    }
    if (notesTooltipHovered && !prevState.notesTooltipHovered) {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  onScroll() {
    const { ID, actions } = this.props;
    actions.reset(`${ID}-order-note-create`);
    window.removeEventListener('scroll', this.onScroll);
  }

  onMoreMenuButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      moreMenuOpen: true,
      deleteMenuOpen: false,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onDeleteMenuItemClick() {
    this.setState({
      deleteMenuOpen: true,
      moreMenuOpen: false,
    });
  }

  onDeleteCancel() {
    this.setState({ anchorEl: null });
  }

  onDeleteSubmit() {
    const { ID, actions } = this.props;
    if (this.props['Is Archive']) {
      actions.deleteArchiveOrder({
        ID,
      })
    } else {
      actions.bulkUpdateOrders({
        values: {
          [ID]: {
            orderDelete: ID,
          },
        },
      });
    }
    this.setState({ anchorEl: null });
  }

  onInsuranceViewButtonClick() {
    this.setState({ insuranceViewDialogOpen: true });
  }

  onInsuranceViewDialogClose() {
    this.setState({ insuranceViewDialogOpen: false });
  }

  onInsuranceEditButtonClick() {
    this.setState({
      insuranceViewDialogOpen: false,
      insuranceEditDialogOpen: true,
    });
  }

  onInsuranceEditDialogClose() {
    this.setState({ insuranceEditDialogOpen: false });
  }

  onPrintMenuItemClick() {
    const { actions, ID } = this.props;
    this.setState({ printDialogOpen: true });
    if (this.props['Is Archive']) {
      actions.fetchArchiveOrder({
        ID,
        triggerLoading: true,
      });
    } else {
      actions.fetchOrder({
        ID,
        triggerLoading: true,
      });
    }
  }

  onPrintDialogClose() {
    this.setState({ printDialogOpen: false });
  }

  onPrintDialogSign(values) {
    this.setState({
      printDialogOpen: false,
      printFormValues: values,
      signDialogOpen: true,
    });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
  }

  onTableRowClick(event) {
    const { classes } = this.props;
    if (event.target.classList.contains(classes.expansionPanelClickable)) {
      this.setState({ expanded: !this.state.expanded });
    }
  }

  onExpandButtonClick() {
    this.setState({ expanded: !this.state.expanded });
  }

  onTableRowMouseEnter() {
    this.setState({
      tooltipsDisabled: false,
      notesTooltipDisabled: false,
    });
  }

  onTableRowMouseLeave() {
    this.setState({
      tooltipsDisabled: true,
      ...!this.state.notesTooltipHovered && !this.state.notesTooltipClicked
        ? { notesTooltipDisabled: true } : {},
    });
  }

  onNotesButtonMouseEnter() {
    this.setState({ notesTooltipHovered: true });
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus({ preventScroll: true });
      }
    }, 100);
  }

  onNotesTooltipMouseEnter() {
    this.setState({ notesTooltipHovered: true });
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus({ preventScroll: true});
      }
    }, 100);
  }

  onNotesButtonFocus() {
    this.setState({
      tooltipsDisabled: false,
      notesTooltipDisabled: false,
      notesTooltipHovered: true,
    });
    if (this.buttonBlurTimeout) {
      clearTimeout(this.buttonBlurTimeout);
    }
    if (this.notesTooltipCloseTimeout) {
      clearTimeout(this.notesTooltipCloseTimeout);
    }
    setTimeout(() => {
      if (this.createOrderNoteInputRef) {
        this.createOrderNoteInputRef.focus({ preventScroll: true });
      }
    }, 100);
  }

  onNotesTooltipMouseLeave() {
    this.setState({ notesTooltipHovered: false });
    this.notesTooltipCloseTimeout = setTimeout(() => {
      if (this.state.tooltipsDisabled && !this.state.notesTooltipClicked) {
        this.setState({ notesTooltipDisabled: true });
      }
    }, 100);
  }

  onNotesButtonMouseLeave() {
    this.setState({ notesTooltipHovered: false });
    this.notesTooltipCloseTimeout = setTimeout(() => {
      if (this.state.tooltipsDisabled && !this.state.notesTooltipClicked) {
        this.setState({ notesTooltipDisabled: true });
      }
    }, 100);
  }

  onOrderNotesSubmitSuccess(values, dispatch, props) {
    this.setState({
      notesTooltipClicked: false,
      notesTooltipHovered: false,
    });
    if (props && props.reset) {
      props.reset();
    }
  }

  onNotesButtonBlur() {
    this.buttonBlurTimeout = setTimeout(() => {
      if (!this.orderNotesRef || !isDescendant(this.orderNotesRef, document.activeElement)) {
        this.setState({ notesTooltipDisabled: false });
      }
      if ((!this.tableRowRef || !isDescendant(this.tableRowRef, document.activeElement))
        && (!this.orderNotesRef || !isDescendant(this.orderNotesRef, document.activeElement))) {
        this.setState({
          tooltipsDisabled: true,
          ...!this.state.notesTooltipHovered && !this.state.notesTooltipClicked
            ? { notesTooltipDisabled: true } : {},
        });
      }
    }, 100);
  }

  onOrderNotesSubmitButtonBlur() {
    this.setState({ notesTooltipHovered: false });
    const menuButtonEl = this.tableRowRef.querySelector(`#menu-button-${this.props.ID}`);
    if (menuButtonEl) {
      menuButtonEl.focus();
    }
  }

  onButtonFocus() {
    if (this.buttonBlurTimeout) {
      clearTimeout(this.buttonBlurTimeout);
    }
    this.setState({
      tooltipsDisabled: false,
      notesTooltipDisabled: false,
    });
  }

  onButtonBlur() {
    this.buttonBlurTimeout = setTimeout(() => {
      if ((!this.tableRowRef || !isDescendant(this.tableRowRef, document.activeElement))
        && (!this.orderNotesRef || !isDescendant(this.orderNotesRef, document.activeElement))) {
        this.setState({
          tooltipsDisabled: true,
          ...!this.state.notesTooltipHovered && !this.state.notesTooltipClicked
            ? { notesTooltipDisabled: true } : {},
        });
      }
    }, 100);
  }

  onStockLineHistoryViewLinkClick(product) {
    this.setState({ stockLineHistoryProduct: product });
  }

  onStockLineHistoryViewDialogClose() {
    this.setState({ stockLineHistoryProduct: null });
  }

  onClaimLogViewButtonClick() {
    this.setState({ claimLogViewDialogOpen: true });
  }

  onClaimLogViewDialogClose() {
    this.setState({ claimLogViewDialogOpen: false });
  }

  onNotesButtonClick() {
    if (!this.state.notesTooltipClicked) {
      document.addEventListener('click', this.onDocumentClick);
    } else {
      document.removeEventListener('click', this.onDocumentClick);
    }
    this.setState({ notesTooltipClicked: !this.state.notesTooltipClicked });
  }

  onDocumentClick(event) {
    if (!isDescendant(this.orderNotesRef, event.target)) {
      document.removeEventListener('click', this.onDocumentClick);
      this.setState({ notesTooltipClicked: false });
    }
  }

  onWatchButtonClick(event) {
    this.setState({
      moreMenuOpen: false,
      deleteMenuOpen: false,
      watcherMenuOpen: true,
      anchorEl: event.currentTarget,
    });
  }

  onArchiveMenuItemClick() {
    const { actions, ID } = this.props;
    actions.archiveOrder({ values: { ID }})
  }

  onUnarchiveMenuItemClick() {
    const { actions, ID } = this.props;
    actions.unarchiveOrder({ values: { ID }})
  }

  onEventLogViewItemClick() {
    this.setState({
      eventLogViewDialogOpen: true,
      anchorEl: null,
    });
  }

  onEventLogViewDialogClose() {
    this.setState({ eventLogViewDialogOpen: false });
  }

  onNotificationsButtonClick() {
    this.setState({ notificationsDialogOpen: true });
  }

  onNotificationsDialogClose() {
    this.setState({ notificationsDialogOpen: false });
  }

  onDocumentView(document) {
    this.setState({
      currentViewDocument: document,
      documentViewDialogOpen: true,
      notificationsDialogOpen: false,
    });
  }

  onDocumentViewDialogClose() {
    this.setState({
      currentViewDocument: null,
      documentViewDialogOpen: false,
      notificationsDialogOpen: true,
    });
  }

  render() {
    const {
      ID,
      Patient,
      Diagnosis,
      Prescriber,
      variant,
      history,
      account,
      classes,
      submitting,
      onTextFieldBlur,
      change,
      onStockLineCreate,
      Insurance,
      business,
      notesTooltipPlacement,
      archive,
      Facility,
      last,
      ...otherProps
    } = this.props;
    const {
      anchorEl,
      moreMenuOpen,
      deleteMenuOpen,
      notesTooltipHovered,
      notesTooltipClicked,
      notesTooltipDisabled,
      expanded,
      insuranceViewDialogOpen,
      insuranceEditDialogOpen,
      printDialogOpen,
      tooltipsDisabled,
      stockLineHistoryProduct,
      signDialogOpen,
      printFormValues,
      claimLogViewDialogOpen,
      watcherMenuOpen,
      eventLogViewDialogOpen,
      notificationsDialogOpen,
      currentViewDocument,
      documentViewDialogOpen,
    } = this.state;
    const notesTooltipOpen = notesTooltipClicked ? true : notesTooltipHovered;
    const specialState = otherProps['Order Line'].length > 0
      ? otherProps['Order Line']
        .map(item => item['Special State'])
        .reduce((accumulator, currentValue) => {
          if (accumulator === 'combined') {
            return accumulator;
          }
          if (accumulator !== currentValue) {
            return 'combined';
          }
          return accumulator;
        })
      : 'none';
    const backorder = otherProps['Order Line'].length > 0
      ? otherProps['Order Line']
        .reduce((accumulator, currentValue) => {
          if (currentValue.Product['Stock Line Balance'] < 0) {
            return true;
          }
          return accumulator;
        }, false)
      : false;
    const containsNarrativeCMNAMB = otherProps['Order Line'].length > 0
      ? otherProps['Order Line'].filter(orderLine =>
        orderLine.Narrative || (orderLine.CMN && orderLine.CMN.ID)).length > 0
      : false;
    const totalReimbs = otherProps['Order Line'].reduce((accumulator, currentValue) => {
      const currentTotalReimbs = currentValue['Order Line Insurance Param']
        .reduce((paramAccumulator, paramCurrentValue) =>
            Insurance.filter(insuranceItem => insuranceItem.ID === paramCurrentValue.Insurance.ID).length !== 0
              ? paramAccumulator + Number(paramCurrentValue.Reimbs)
              : paramAccumulator
        , 0);
      return accumulator + currentTotalReimbs;
    }, 0);
    const totalCharge = otherProps['Order Line'].reduce((accumulator, currentValue) =>
      accumulator + (currentValue.Charge * currentValue.Units)
      , 0);
    const { transmissionStatusType, transmissionStatus } = otherProps['Order Line'].reduce((accumulator, currentValue) => {
      if (currentValue['Claim Md Claim'] && currentValue['Claim Md Claim'][0] && currentValue['Claim Md Claim'][0].Status) {
        const claimStatus = currentValue['Claim Md Claim'][0].Status[0].Status;
        const claimStatusCategory = currentValue['Claim Md Claim'][0].Status[0].Category;
        const claimLinkAt = formatUtcToLocalDate(
          currentValue['Claim Md Claim'][0].Status[0]['Link At'],
          timeDateFormat,
          dateFormat,
          business['Company Address']['Time Zone'],
          business['Company Address']['Observes Daylight Saving'],
        );
        switch (claimStatusCategory) {
          case 'Sent':
            return {
              transmissionStatusType: {
                ...accumulator.transmissionStatusType,
                send: true,
              },
              transmissionStatus: [
                ...accumulator.transmissionStatus,
                `${claimStatus} ${claimLinkAt}`,
              ],
            };
          case 'Success':
            return {
              transmissionStatusType: {
                ...accumulator.transmissionStatusType,
                approved: true,
              },
              transmissionStatus: [
                ...accumulator.transmissionStatus,
                `${claimStatus} ${claimLinkAt}`,
              ],
            };
          case 'Error':
            return {
              transmissionStatusType: {
                ...accumulator.transmissionStatusType,
                rejected: true,
              },
              transmissionStatus: [
                ...accumulator.transmissionStatus,
                `${claimStatus} ${claimLinkAt}`,
              ],
            };
          default:
            return {
              ...accumulator,
              transmissionStatus: [
                ...accumulator.transmissionStatus,
                `${claimStatus} ${claimLinkAt}`,
              ],
            };
        }
      }
      return {
        ...accumulator,
        transmissionStatusType: {
          ...accumulator.transmissionStatusType,
          unsend: true,
        },
      };
    }, {
      transmissionStatusType: {
        unsend: false,
        created: false,
        approved: false,
        send: false,
        rejected: false,
      },
      transmissionStatus: [],
    });
    const State = otherProps['Order Line'].reduce((accumulator, currentValue) => ([
      ...accumulator,
      ...currentValue['Workflow Status']
    ]), []).reduce((accumulator, currentValue) => {
      if (accumulator.filter(item => item.ID === currentValue['Workflow State'].ID).length === 0) {
        return [
          ...accumulator,
          currentValue['Workflow State'],
        ];
      }
      return accumulator;
    }, []);
    const StateID = State.map(item => item.ID);
    const claim = uniqBy(otherProps['Order Line'].reduce((accumulator, currentItem) => {
      if (currentItem['Claim Md Claim'] && currentItem['Claim Md Claim'].length > 0) {
        return [
          ...accumulator,
          ...currentItem['Claim Md Claim'],
        ];
      }
      return accumulator;
    }, []), 'ID');
    const watchedByUser = Patient.Watcher && Patient.Watcher.filter(item => item.ID === account.ID).length > 0;
    const phoneContacts = otherProps['Primary Insurance']['Insurance Info']
      && otherProps['Primary Insurance']['Insurance Info'].Contacts
      ? otherProps['Primary Insurance']['Insurance Info'].Contacts
      .filter(item => item.type === 'Phone')
      .map(item => ({
        Value: item.value,
        Description: item.description,
      })) : [];
    const faxContacts = otherProps['Primary Insurance']['Insurance Info']
      && otherProps['Primary Insurance']['Insurance Info'].Contacts
      ? otherProps['Primary Insurance']['Insurance Info'].Contacts
      .filter(item => item.type === 'Fax')
      .map(item => ({
          Value: item.value,
          Description: item.description,
        })) : [];
    const emailContacts = otherProps['Primary Insurance']['Insurance Info']
      && otherProps['Primary Insurance']['Insurance Info'].Contacts
      ? otherProps['Primary Insurance']['Insurance Info'].Contacts
      .filter(item => item.type === 'Email')
      .map(item => ({
          Value: item.value,
          Description: item.description,
        })) : [];
    return (
      <Fragment>
        <RootRef
          rootRef={(tableRowRef) => {
            this.tableRowRef = tableRowRef;
          }}
        >
          <TableRow
            className={classNames(classes.tableRow, classes.expansionPanelClickable, {
              [classes.tableRowArchive]: otherProps['Is Archive'],
            })}
            hover
            onClick={this.onTableRowClick}
            onMouseEnter={this.onTableRowMouseEnter}
            onMouseLeave={this.onTableRowMouseLeave}
            key={`${ID}-summary`}
          >
            <TableCell
              className={classNames(classes.expansionPanelClickable, classes.cellExpansionActions, {
                [classes.cellExpansionActionsNotification]: backorder
                  || !Patient['Exists Address'],
              })}
            >
              <div
                className={classNames(classes.states, {
                  [classes.statesLong]: StateID.length > 4,
                })}
              >
                {StateID.indexOf(1) !== -1 && (
                  <Typography className={classNames(classes.state, classes.stateIntake)}>
                    I
                  </Typography>
                )}
                {StateID.indexOf(2) !== -1 && (
                  <Typography className={classNames(classes.state, classes.stateDelivery)}>
                    D
                  </Typography>
                )}
                {StateID.indexOf(3) !== -1 && (
                  <Typography className={classNames(classes.state, classes.stateBilling)}>
                    B
                  </Typography>
                )}
                {StateID.indexOf(4) !== -1 && (
                  <Typography className={classNames(classes.state, classes.stateCollection)}>
                    C
                  </Typography>
                )}
                {StateID.indexOf(5) !== -1 && (
                  <Typography className={classNames(classes.state, classes.statePaid)}>
                    P
                  </Typography>
                )}
              </div>
              {variant !== 'connected' &&
                <div className={classes.orderSelection}>
                  <Field
                    id={`${ID}-order-selected`}
                    name={`${ID}-order-selected`}
                    component={CheckboxField}
                    disabled={otherProps['Is Archive']}
                  />
                </div>
              }
              <Typography
                className={classNames(classes.expansionPanelClickable,
                  classes.typographyOrderLinesCount)}
              >
                {otherProps['Order Line'].length}
              </Typography>
              <IconButton
                className={classes.buttonExpand}
                onClick={this.onExpandButtonClick}
                onFocus={this.onButtonFocus}
                onBlur={this.onButtonBlur}
              >
                <IconArrowHeadBottom
                  className={classNames(classes.iconArrowHeadBottom, {
                    [classes.iconArrowHeadBottomExpanded]: expanded,
                  })}
                />
              </IconButton>
            </TableCell>  
            <TableCell
              className={classNames(classes.expansionPanelClickable,
                classes.cellSpecialState)}
            >
              {['combined', 'current'].indexOf(specialState) !== -1 &&
                <Badge
                  color="active"
                  tooltipText={firstCharToUpperCase(specialState)}
                >
                  C
                </Badge>
              }
              {['combined', 'future'].indexOf(specialState) !== -1 &&
                <Badge
                  color="future"
                  tooltipText={firstCharToUpperCase(specialState)}
                >
                  F
                </Badge>
              }
            </TableCell>
            <TableCell className={classNames(classes.expansionPanelClickable, classes.cellOrderNumber)}>
              {formatOrderNumber(ID)}
            </TableCell>
            {variant !== 'combined' &&
              <TableCell
                className={classNames(classes.expansionPanelClickable, classes.cellPatient)}
              >
                {Patient && Patient['Record Number'] ? (
                  <Fragment>
                    <Tooltip
                      content={
                        <Typography>
                          {Patient['Patient Status'] === true && 'Active'}
                          {(Patient['Patient Status'] === false && Patient['Date of Death']) && `Inactive, DOD: ${Patient['Date of Death']}`}
                          {(Patient['Patient Status'] === false && !Patient['Date of Death']) && 'Inactive'}
                        </Typography>
                      }
                      placement="top"
                    >
                      <div
                        className={classNames(classes.statusIcon, {
                          [classes.statusIconActive]: Patient['Patient Status'] === true,
                          [classes.statusIconInactive]: Patient['Patient Status'] === false,
                        })}
                      />
                    </Tooltip>
                    <Link
                      className={classes.link}
                      href={`/charts/${Patient['Record Number']}`}
                      onFocus={this.onButtonFocus}
                      onBlur={this.onButtonBlur}
                      target="_blank"
                    >
                      {`${splitLongWords(`${Patient['Last Name']}, ${Patient['First Name']}`)}, ${Patient['Date of Birth']}`}
                    </Link>
                  </Fragment>
                ) : (
                  <div className={classes.patientDeleted}>
                    Patient Deleted
                  </div>
                )}
              </TableCell>
            }
            <TableCell className={classNames(classes.expansionPanelClickable,
              classes.cellPrescriber)}
            >
              {(Prescriber && Prescriber.ID) &&
                splitLongWords(formatPrescriberName(Prescriber), 14)
              }
            </TableCell>
            <TableCell
              className={classNames(classes.expansionPanelClickable, classes.cellInsurance)}
            >
              {(otherProps['Primary Insurance'] && otherProps['Primary Insurance'].ID) ? (
                <Fragment>
                  <Link
                    onClick={this.onInsuranceViewButtonClick}
                    onFocus={this.onButtonFocus}
                    onBlur={this.onButtonBlur}
                  >
                    {otherProps['Primary Insurance']['Order Benefits'] &&
                      <Badge color="primary" gutter="right">
                        {otherProps['Primary Insurance']['Order Benefits'] === 'Primary' && '1'}
                        {otherProps['Primary Insurance']['Order Benefits'] === 'Secondary' && '2'}
                        {otherProps['Primary Insurance']['Order Benefits'] === 'Secondary As Primary' && '2'}
                        {otherProps['Primary Insurance']['Order Benefits'] === 'Tertiary' && '3'}
                      </Badge>
                    }
                    {otherProps['Primary Insurance']['Insurance Type'] === 'Self'
                      ? 'Self Payer'
                      : otherProps['Primary Insurance']['Payer Name']}
                  </Link>
                  {Insurance.length > 1 ? (
                    <Tooltip
                      content={
                        <OrdersTableRowInsurances
                          Insurance={uniqBy([
                            otherProps['Primary Insurance'],
                            ...Insurance,
                          ], 'ID')}
                          {...otherProps['Order Insurance Param'] && otherProps['Order Insurance Param'].length > 0 ? {
                            'Order Insurance Param': otherProps['Order Insurance Param'],
                          } : {}}
                        />
                      }
                      placement="top"
                      disabled={tooltipsDisabled}
                    >
                      <Chip
                        className={classNames(classes.chip,
                          classes.expansionPanelClickable)}
                        color="primary"
                        borderColor="primary"
                        label={`+${Insurance.length - 1}`}
                      />
                      {(otherProps['Order Insurance Param']
                        && otherProps['Order Insurance Param'].length > 0
                        && otherProps['Order Insurance Param'].filter(paramsItem => paramsItem.Authorization).length > 0) && (
                        <div className={classes.iconProrAuth}>
                          <IconDocument className={classes.iconDocument} />
                          <IconThumbUp className={classes.iconThumbUp} />
                        </div>
                      )}
                    </Tooltip>
                  ) : (
                    <Fragment>
                      {(otherProps['Order Insurance Param']
                        && otherProps['Order Insurance Param'][0]
                        && otherProps['Order Insurance Param'][0].Authorization) && (
                        <Tooltip
                          content={
                            <Typography>
                              {`Authorization: ${otherProps['Order Insurance Param'][0].Authorization}`}
                              {otherProps['Order Insurance Param'][0]['Authorization From'] &&
                                ` (${otherProps['Order Insurance Param'][0]['Authorization From']}-${otherProps['Order Insurance Param'][0]['Authorization To']})`}
                            </Typography>
                          }
                          placement="top"
                          disabled={tooltipsDisabled}
                        >
                          <div className={classes.iconProrAuth}>
                            <IconDocument className={classes.iconDocument} />
                            <IconThumbUp className={classes.iconThumbUp} />
                          </div>
                        </Tooltip>
                      )}
                    </Fragment>
                  )}
                  <InsuranceViewDialog
                    open={insuranceViewDialogOpen}
                    onClose={this.onInsuranceViewDialogClose}
                    onEditButtonClick={this.onInsuranceEditButtonClick}
                    {...{ 'User Record Number': Patient['Record Number'] }}
                    {...otherProps['Primary Insurance']}
                  />
                  <InsuranceCreateEditDialog
                    open={insuranceEditDialogOpen}
                    onClose={this.onInsuranceEditDialogClose}
                    onSubmitSuccess={this.onInsuranceEditDialogClose}
                    form={`${otherProps['Primary Insurance'].ID}-insuranceEdit`}
                    ID={otherProps['Primary Insurance'].ID}
                    {...{ 'User Record Number': Patient['Record Number'] }}
                    {...{ 'Checking Eligibility': otherProps['Primary Insurance']['Checking Eligibility'] }}
                    initialValues={otherProps['Primary Insurance']['Insurance ID']
                      ? {
                        'Payer Name': otherProps['Primary Insurance']['Insurance Info']['Payer Name'],
                        'Insurance Info ID': otherProps['Primary Insurance']['Insurance Info'].ID,
                        'Insurance Info Payer Name': otherProps['Primary Insurance']['Insurance Info']['Payer Name'],
                        'Transmission Method': otherProps['Primary Insurance']['Transmission Method'],
                        'Insurance ID': otherProps['Primary Insurance']['Insurance ID'],
                        'Group ID': otherProps['Primary Insurance']['Group ID'],
                        ...otherProps['Primary Insurance']['Claim Filling Code']
                          && otherProps['Primary Insurance']['Claim Filling Code'].ID
                          ? {
                            'Claim Filling Code ID': otherProps['Primary Insurance']['Claim Filling Code'].ID,
                            'Claim Filling Code Name': otherProps['Primary Insurance']['Claim Filling Code']['Full Name'],
                            'Selected Claim Filling Code Name': otherProps['Primary Insurance']['Claim Filling Code']['Full Name'],
                          } : {},
                        ...otherProps['Primary Insurance']['Order Benefits']
                          ? { 'Order Benefits': otherProps['Primary Insurance']['Order Benefits'] }
                          : {},
                        'Effective From': otherProps['Primary Insurance']['Effective From'],
                        'Effective To': otherProps['Primary Insurance']['Effective To'],
                        'Relation to Insured': otherProps['Primary Insurance']['Relation to Insured'],
                        ...otherProps['Primary Insurance']['Copay Type']
                          ? { 'Copay Type': otherProps['Primary Insurance']['Copay Type'] }
                          : {},
                        ...otherProps['Primary Insurance']['Copay Type']
                          && otherProps['Primary Insurance']['Copay Type'] === 'Fixed'
                          ? { 'Copay Amount Fixed': formatPrice(otherProps['Primary Insurance']['Copay Amount']) }
                          : {},
                        ...otherProps['Primary Insurance']['Copay Type']
                          && otherProps['Primary Insurance']['Copay Type'] === 'Percent'
                          ? { 'Copay Amount Percent': `${otherProps['Primary Insurance']['Copay Amount']}%` }
                          : {},
                        Comments: otherProps['Primary Insurance'].Comments,
                        'Insurance Status': otherProps['Primary Insurance']['Insurance Status'] === 'Inactive' ? false : true,
                        'Related First Name': otherProps['Primary Insurance']['Related First Name'],
                        'Related Last Name': otherProps['Primary Insurance']['Related Last Name'],
                        'Related Date of Birth': otherProps['Primary Insurance']['Related Date of Birth'],
                        'Related Insurance ID': otherProps['Primary Insurance']['Related Insurance ID'],
                        'Contacts Phone': phoneContacts.length > 0 ? phoneContacts : [
                          {
                            Value: '',
                            Description: '',
                          },
                        ],
                        'Contacts Fax': faxContacts.length > 0 ? faxContacts : [
                          {
                            Value: '',
                            Description: '',
                          },
                        ],
                        'Contacts Email': emailContacts.length > 0 ? emailContacts : [
                          {
                            Value: '',
                            Description: '',
                          },
                        ],
                      } : {}}
                  />
                </Fragment>
              ) : 'Self Payer'}
            </TableCell>
            <TableCell className={classNames(classes.cell,
              classes.cellDateOfService, classes.expansionPanelClickable)}
            >
              <Link
                className={classes.link}
                onClick={() => {
                  if (otherProps['Is Archive']) {
                    window.open(`/workflow/archive_order/edit/${ID}`, '_blank');
                  } else {
                    window.open(`/workflow/order/edit/${ID}`, '_blank');
                  }
                }}
                onFocus={this.onButtonFocus}
                onBlur={this.onButtonBlur}
                inlineLabel
                breakWord
                target="_blank"
              >
                {otherProps['Order Line'].length > 0 &&
                  uniq(otherProps['Order Line']
                    .map(item => `${item['Date of Service From']} - ${item['Date of Service To']}`))
                    .map(item => (
                      <div
                        key={item}
                        className={classNames(classes.dateOfService,
                          classes.expansionPanelClickable)}
                      >
                        {item}
                      </div>
                    ))
                }
              </Link>
            </TableCell>
            <TableCell
              className={classNames(classes.cellHCPCSCode,
                classes.expansionPanelClickable)}
            >
              {otherProps['Order Line'].length > 0 &&
                otherProps['Order Line'].reduce((accumulator, currentValue) => {
                  if (!accumulator.find(item => item['HCPC S Code'].ID === currentValue['HCPC S Code'].ID)) {
                    return [
                      ...accumulator,
                      currentValue,
                    ];
                  }
                  return accumulator;
                }, [])
                  .map((item, index, array) => (
                    <Fragment key={item['HCPC S Code']['HCPC S Code']}>
                      {index > 0 && ' '}
                      <Link
                        onClick={() => {
                          this.onStockLineHistoryViewLinkClick(item.Product);
                        }}
                      >
                        {item['HCPC S Code']['HCPC S Code']}
                        {(array.length > 1 && index < (array.length - 1)) && ','}
                      </Link>
                    </Fragment>
                  ))
              }
              <InventoryStockLineHistoryViewDialog
                open={Boolean(stockLineHistoryProduct)}
                onClose={this.onStockLineHistoryViewDialogClose}
                onStockLineEdit={this.onStockLineEdit}
                {...stockLineHistoryProduct ? {
                  Product: otherProps['Order Line'].find(item =>
                    item.Product.ID === stockLineHistoryProduct.ID).Product,
                } : {}}
                business={business}
              />
            </TableCell>
            {['workflow', 'combined'].indexOf(variant) !== -1 &&
              <TableCell
                className={classNames(classes.cellTotalCharge,
                  classes.expansionPanelClickable)}
              >
                {totalCharge !== 0 ? formatPrice(totalCharge) : 0}
              </TableCell>
            }
            {['workflow', 'combined'].indexOf(variant) !== -1 &&
              <TableCell
                className={classNames(classes.cellTotalReimbs,
                  classes.expansionPanelClickable)}
              >
                {totalReimbs !== 0 ? formatPrice(totalReimbs) : 0}
              </TableCell>
            }
            <TableCell
              className={classNames(classes.cellSales,
                classes.expansionPanelClickable, {
                  [classes.cellSalesVariantCombined]: variant === 'combined',
                })}
            >
              {otherProps['Sales Representative'].length > 0 &&
                splitLongWords(`${otherProps['Sales Representative'][0]['First Name']}
                ${otherProps['Sales Representative'][0]['Last Name']}`, 13)
              }
              {otherProps['Sales Representative'].length > 1 &&
                <Tooltip
                  content={
                    <OrdersTableRowSalesRepresentatives
                      {...{ 'Sales Representative': otherProps['Sales Representative'] }}
                    />
                  }
                  placement="top"
                  disabled={tooltipsDisabled}
                >
                  <Chip
                    className={classNames(classes.chip,
                      classes.expansionPanelClickable)}
                    color="primary"
                    borderColor="primary"
                    label={`+${otherProps['Sales Representative'].length - 1}`}
                  />
                </Tooltip>
              }
            </TableCell>
            {['workflow', 'combined'].indexOf(variant) !== -1 &&
              <TableCell className={classNames(classes.cell,
                classes.cellDateOfDelivery, classes.expansionPanelClickable)}
              >
                {(otherProps['Order Line'].length > 0
                  && otherProps['Order Line']
                    .filter(item => item['Date of Delivery']).length > 0) ? (
                    uniq(otherProps['Order Line']
                      .filter(item => item['Date of Delivery'])
                      .map(item => moment(item['Date of Delivery'], timeDateFormat).format(dateFormat)))
                      .map(item => (
                        <div
                          key={item}
                          className={classNames(classes.dateOfService,
                            classes.expansionPanelClickable)}
                        >
                          {item}
                        </div>
                      ))
                  ) : (
                    'Not Set'
                  )}
              </TableCell>
            }
            <TableCell
              className={classNames(classes.cellCreationDate,
                classes.expansionPanelClickable)}
            >
              <Tooltip
                content={
                  <Typography>
                    {otherProps['Date of Creation']}
                  </Typography>
                }
                placement="top"
                disabled={tooltipsDisabled}
              >
                {moment().diff(moment(otherProps['Date of Creation'], dateFormat), 'days')}
              </Tooltip>
            </TableCell>
            <TableCell
              className={classNames(classes.cellFacility,
                classes.expansionPanelClickable)}
            >
              {(Facility && Facility.ID) &&
                 <Tooltip
                  content={
                    <Typography>
                      {Facility['Company Info']['Company Name']}
                    </Typography>
                  }
                  placement="top"
                >
                  <img
                    className={classes.facilityLogo}
                    src={`${Facility.Image['File Url']}${Facility.Image['File Name']}`}
                  />
                </Tooltip>
              }
            </TableCell>
            <TableCell
              className={classNames(classes.cellTransmission,
                classes.expansionPanelClickable, {
                  [classes.tooltipTransmissionRejectedUnsend]: !transmissionStatusType.created
                    && !transmissionStatusType.approved && !transmissionStatusType.send
                    && transmissionStatusType.rejected && transmissionStatusType.unsend,
                  [classes.tooltipTransmissionСreatedUnsend]: transmissionStatusType.created
                    && !transmissionStatusType.approved && !transmissionStatusType.send
                    && !transmissionStatusType.rejected && transmissionStatusType.unsend,
                  [classes.tooltipTransmissionSendUnsend]: !transmissionStatusType.created
                    && !transmissionStatusType.approved && transmissionStatusType.send
                    && !transmissionStatusType.rejected && transmissionStatusType.unsend,
                  [classes.tooltipTransmissionApprovedUnsend]: !transmissionStatusType.created
                    && transmissionStatusType.approved && !transmissionStatusType.send
                    && !transmissionStatusType.rejected && transmissionStatusType.unsend,
                  [classes.tooltipTransmissionApprovedСreated]: transmissionStatusType.created
                    && transmissionStatusType.approved && !transmissionStatusType.send
                    && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionRejectedСreated]: transmissionStatusType.created
                    && !transmissionStatusType.approved && !transmissionStatusType.send
                    && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionСreatedSend]: transmissionStatusType.created
                    && !transmissionStatusType.approved && transmissionStatusType.send
                    && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionApprovedSend]: !transmissionStatusType.created
                    && transmissionStatusType.approved && transmissionStatusType.send
                    && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionRejectedSend]: !transmissionStatusType.created
                    && !transmissionStatusType.approved && transmissionStatusType.send
                    && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionRejectedApproved]: !transmissionStatusType.created
                    && transmissionStatusType.approved && !transmissionStatusType.send
                    && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionCreatedSendApprovedRejected]:
                  (transmissionStatusType.created
                    && transmissionStatusType.approved && transmissionStatusType.send
                    && transmissionStatusType.rejected && transmissionStatusType.unsend)
                    || (transmissionStatusType.created
                      && transmissionStatusType.approved && transmissionStatusType.send
                      && transmissionStatusType.rejected && !transmissionStatusType.unsend),
                  [classes.tooltipTransmissionCreatedSendApproved]: transmissionStatusType.created
                    && transmissionStatusType.approved && transmissionStatusType.send
                    && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionCreatedSendRejected]: transmissionStatusType.created
                    && !transmissionStatusType.approved && transmissionStatusType.send
                    && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionCreatedApprovedRejected]:
                  transmissionStatusType.created
                    && transmissionStatusType.approved && !transmissionStatusType.send
                    && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionSendApprovedRejected]: !transmissionStatusType.created
                    && transmissionStatusType.approved && transmissionStatusType.send
                    && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                  [classes.tooltipTransmissionCreatedSendApprovedUnsend]:
                  transmissionStatusType.created
                    && transmissionStatusType.approved && transmissionStatusType.send
                    && !transmissionStatusType.rejected && transmissionStatusType.unsend,
                  [classes.tooltipTransmissionCreatedSendRejectedUnsend]:
                  transmissionStatusType.created
                    && !transmissionStatusType.approved && transmissionStatusType.send
                    && transmissionStatusType.rejected && transmissionStatusType.unsend,
                  [classes.tooltipTransmissionCreatedApprovedRejectedUnsend]:
                  transmissionStatusType.created
                    && transmissionStatusType.approved && !transmissionStatusType.send
                    && transmissionStatusType.rejected && transmissionStatusType.unsend,
                  [classes.tooltipTransmissionSendApprovedRejectedUnsend]:
                  !transmissionStatusType.created
                    && transmissionStatusType.approved && transmissionStatusType.send
                    && !transmissionStatusType.rejected && transmissionStatusType.unsend,
                })}
            >
              <Tooltip
                className={classes.tooltipTransmission}
                disabled={tooltipsDisabled}
                content={
                  <Typography>
                    {otherProps['Transmission Method']}
                    {(transmissionStatus
                      && transmissionStatus.length > 0) && `: ${uniq(transmissionStatus).join(', ')}`}
                  </Typography>
                }
                placement="top"
              >
              {otherProps['Transmission Method'] === 'Electronically'
                && claim.length > 0 ? (
                  <IconButton
                    className={classes.claimLogViewButton}
                    onClick={this.onClaimLogViewButtonClick}
                  >
                    <Typography
                      className={classNames(classes.typographyTransmission, {
                        [classes.typographyTransmissionCreated]: transmissionStatusType.created
                          && !transmissionStatusType.approved && !transmissionStatusType.send
                          && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                        [classes.typographyTransmissionApproved]: !transmissionStatusType.created
                          && transmissionStatusType.approved && !transmissionStatusType.send
                          && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                        [classes.typographyTransmissionSend]: !transmissionStatusType.created
                          && !transmissionStatusType.approved && transmissionStatusType.send
                          && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                        [classes.typographyTransmissionRejected]: !transmissionStatusType.created
                          && !transmissionStatusType.approved && !transmissionStatusType.send
                          && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                      })}
                      variant="caption"
                    >
                      {otherProps['Transmission Method'].slice(0, 1)}
                    </Typography>
                  </IconButton>
                ) : (
                  <Typography
                    className={classNames(classes.typographyTransmission, {
                      [classes.typographyTransmissionCreated]: transmissionStatusType.created
                        && !transmissionStatusType.approved && !transmissionStatusType.send
                        && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                      [classes.typographyTransmissionApproved]: !transmissionStatusType.created
                        && transmissionStatusType.approved && !transmissionStatusType.send
                        && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                      [classes.typographyTransmissionSend]: !transmissionStatusType.created
                        && !transmissionStatusType.approved && transmissionStatusType.send
                        && !transmissionStatusType.rejected && !transmissionStatusType.unsend,
                      [classes.typographyTransmissionRejected]: !transmissionStatusType.created
                        && !transmissionStatusType.approved && !transmissionStatusType.send
                        && transmissionStatusType.rejected && !transmissionStatusType.unsend,
                    })}
                    variant="caption"
                  >
                    {otherProps['Transmission Method'].slice(0, 1)}
                  </Typography>
                )}
                <div className={classes.createdFourth} />
                <div className={classes.sendFourth} />
                <div className={classes.approvedFourth} />
                <div className={classes.rejectedFourth} />
                <div className={classes.createdHalf} />
                <div className={classes.sendHalf} />
                <div className={classes.approvedHalf} />
                <div className={classes.rejectedHalf} />
              </Tooltip>
              {claim.length > 0 &&
                <ClaimLogViewDialog
                  open={claimLogViewDialogOpen}
                  onClose={this.onClaimLogViewDialogClose}
                  {...{
                    'Order ID': ID,
                    'Order Line ID': otherProps['Order Line'].map(item => item.ID),
                  }}
                  Claim={claim}
                  Insurance={otherProps['Primary Insurance']}
                  Patient={Patient}
                  business={business}
                />
              }
            </TableCell>
            <TableCell
              className={classNames(classes.cellButton,
                classes.expansionPanelClickable)}
            >
              <Tooltip
                popperClassName={classNames(classes.noteTooltipPopper, {
                  [classes.noteTooltipPopperLast]: last,
                })}
                content={
                  <OrderNotes
                    {...{ 'Author ID': account.ID }}
                    {...{ 'Order ID': ID }}
                    {...{ 'Order Notes': otherProps.Notes }}
                    onSubmitSuccess={this.onOrderNotesSubmitSuccess}
                    onSubmitButtonBlur={this.onOrderNotesSubmitButtonBlur}
                    rootRef={(orderNotesRef) => {
                      this.orderNotesRef = orderNotesRef;
                    }}
                    createInputRef={(createOrderNoteInputRef) => {
                      this.createOrderNoteInputRef = createOrderNoteInputRef;
                    }}
                    business={business}
                    disabled={otherProps['Is Archive']}
                  />
                }
                placement={notesTooltipPlacement}
                disabled={notesTooltipDisabled}
                disableHoverListener
                open={notesTooltipOpen}
                disablePointerEvents={false}
                onMouseLeave={this.onNotesTooltipMouseLeave}
                onMouseEnter={this.onNotesTooltipMouseEnter}
              >
                <IconButton
                  className={classNames({
                    [classes.buttonNotesTooltipOpen]: notesTooltipOpen,
                    [classes.buttonNotesWithNotes]: otherProps.Notes.length > 0,
                  })}
                  onMouseOver={this.onNotesButtonMouseEnter}
                  onMouseLeave={this.onNotesButtonMouseLeave}
                  onFocus={this.onNotesButtonFocus}
                  onBlur={this.onNotesButtonBlur}
                  onClick={this.onNotesButtonClick}
                >
                  <IconBlurb className={classes.iconBlurb} />
                  {(otherProps.Notes && otherProps.Notes.length > 0) &&
                    <Typography
                      className={classes.typographyNotes}
                      variant="caption"
                    >
                      {otherProps.Notes.length}
                    </Typography>
                  }
                </IconButton>
              </Tooltip>
            </TableCell>
            {variant === 'workflow' &&
              <TableCell
                className={classNames(classes.cellButton,
                  classes.expansionPanelClickable)}
              >
                <IconButton
                  tooltipContent="Notifications"
                  onClick={this.onNotificationsButtonClick}
                >
                  <div className={classes.iconSMS}>
                    <IconBlurbs className={classes.iconBlurbs} />
                    <Typography
                      className={classes.typographySMS}
                    >
                      SMS
                    </Typography>
                    {(Patient['Unread SMS'] && Patient['Unread SMS'] > 0) ? (
                      <Typography
                        className={classes.typographyNewNotification}
                      >
                        {Patient['Unread SMS']}
                      </Typography>
                    ) : ''}
                  </div>
                </IconButton>
                <NotificationsDialog
                  open={notificationsDialogOpen}
                  onClose={this.onNotificationsDialogClose}
                  {...Patient}
                  business={business}
                  onDocumentView={this.onDocumentView}
                />
                {currentViewDocument && currentViewDocument.ID &&
                  <DocumentsViewDialog
                    open={documentViewDialogOpen}
                    onClose={this.onDocumentViewDialogClose}
                    Documents={[currentViewDocument]}
                    initialDocumentID={currentViewDocument.ID}
                    patient={Patient}
                  />
                }
              </TableCell>
            }
            {variant === 'workflow' &&
              <TableCell
                className={classNames(classes.cellButton,
                  classes.expansionPanelClickable)}
              >
                <IconButton
                  className={classNames(classes.iconButton, {
                    [classes.iconButtonActive]: watchedByUser,
                  })}
                  aria-label="More"
                  aria-owns={watcherMenuOpen && Boolean(anchorEl) ? `${Patient['Record Number']}-watcher-menu` : null}
                  aria-haspopup="true"
                  onClick={this.onWatchButtonClick}
                  tooltipContent="Watching"
                >
                  <IconWatch className={classes.iconWatch} />
                  <Typography
                    variant="caption"
                    className={classNames(classes.typographyCount, {
                      [classes.typographyCountActive]: watchedByUser,
                    })}
                  >
                    {Patient.Watcher && Patient.Watcher.length > 0 ? Patient.Watcher.length : ''}
                  </Typography>
                </IconButton>
                <Menu
                  id={`${Patient['Record Number']}-watcher-menu`}
                  anchorEl={anchorEl}
                  open={watcherMenuOpen && Boolean(anchorEl)}
                  onClose={this.onMenuClose}
                  maxHeight={400}
                >
                  <Watchers
                    patient={Patient}
                    onClose={this.onMenuClose}
                  />
                </Menu>
              </TableCell>
            }
            <TableCell
              className={classNames(classes.cellButton,
                classes.expansionPanelClickable)}
            >
              <IconButton
                className={classes.iconButtonMenu}
                id={`menu-button-${ID}`}
                aria-label="More"
                aria-owns={Boolean(anchorEl) && (moreMenuOpen || deleteMenuOpen)
                  ? `${ID}-order-more-menu` : null}
                aria-haspopup="true"
                onClick={this.onMoreMenuButtonClick}
                tooltipContent={tooltipsDisabled ? null : 'Open Menu'}
                onFocus={this.onButtonFocus}
                onBlur={this.onButtonBlur}
              >
                <IconMoreVert className={classes.iconMoreVert} />
              </IconButton>
              <Menu
                id={`${ID}-order-more-menu`}
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && (moreMenuOpen || deleteMenuOpen)}
                onClose={this.onMenuClose}
                list={!deleteMenuOpen}
                maxHeight={342}
              >
                {deleteMenuOpen ? (
                  <DeleteMenu
                    text="Are you sure you want to delete this order?"
                    onCancel={this.onDeleteCancel}
                    onSubmit={this.onDeleteSubmit}
                  />
                ) : (
                  <Fragment>
                    {otherProps['Is Archive'] &&
                      <MenuItem
                        onClick={() => {
                          this.onUnarchiveMenuItemClick();
                          this.onMenuClose();
                        }}
                      >
                        Extract from Archive
                      </MenuItem>
                    }
                    <MenuItem
                      onClick={() => {
                        if (otherProps['Is Archive']) {
                          window.open(`/workflow/archive_order/view/${ID}`, '_blank');
                        } else {
                          window.open(`/workflow/order/view/${ID}`, '_blank');
                        }
                        this.onMenuClose();
                      }}
                      disabled={!Patient['Record Number']}
                    >
                      View Order
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        if (otherProps['Is Archive']) {
                          window.open(`/workflow/archive_order/edit/${ID}`, '_blank');
                        } else {
                          window.open(`/workflow/order/edit/${ID}`, '_blank');
                        }
                        this.onMenuClose();
                      }}
                      disabled={!Patient['Record Number']}
                    >
                      Edit Order
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        window.open(`/workflow/order/copy/${ID}`, '_blank');
                        this.onMenuClose();
                      }}
                      disabled={!Patient['Record Number'] || otherProps['Is Archive']}
                    >
                      Copy Order
                    </MenuItem>
                    <MenuItem
                      onClick={this.onPrintMenuItemClick}
                      disabled={!Patient['Record Number']}
                    >
                      Print Data
                    </MenuItem>
                    {!otherProps['Is Archive'] &&
                      <MenuItem
                        onClick={() => {
                          this.onArchiveMenuItemClick();
                          this.onMenuClose();
                        }}
                      >
                        Archive Order
                      </MenuItem>
                    }
                    <MenuItem
                      onClick={this.onEventLogViewItemClick}
                      disabled={!Patient['Record Number']}
                    >
                      View Activity
                    </MenuItem>
                    <MenuItem
                      onClick={this.onDeleteMenuItemClick}
                      color="warning"
                      disabled={account.Roles.filter(item =>
                        item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                    >
                      Delete Order
                    </MenuItem>
                  </Fragment>
                )}
              </Menu>
              {Patient && Patient['Record Number'] && (
                <Fragment>
                  <PrintDialog
                    form={`${ID}-orderPrint`}
                    variant="order"
                    open={printDialogOpen}
                    onClose={this.onPrintDialogClose}
                    onSign={this.onPrintDialogSign}
                    ID={ID}
                    patient={Patient}
                    order={this.props}
                    diagnosis={Diagnosis}
                    prescriber={Prescriber}
                    facility={Facility}
                    {...{
                      'Order Line': otherProps['Order Line'],
                      'Primary Insurance': otherProps['Primary Insurance'],
                      'Order Insurance Param': otherProps['Order Insurance Param'],
                      'Is Archive': otherProps['Is Archive'],
                    }}
                  />
                  <DocumentsSignDialog
                    open={signDialogOpen}
                    onClose={this.onSignDialogClose}
                    printFormValues={printFormValues}
                    variant="order"
                    ID={ID}
                    patient={Patient}
                    prescriber={Prescriber}
                    order={this.props}
                    {...{
                      'Order Line': otherProps['Order Line'],
                      'Primary Insurance': otherProps['Primary Insurance'],
                      'Order Insurance Param': otherProps['Order Insurance Param'],
                    }}
                  />
                  <EventLogViewDialog
                    open={eventLogViewDialogOpen}
                    onClose={this.onEventLogViewDialogClose}
                    {...Patient}
                    business={business}
                    {...{ 'Order ID': ID }}
                  />
                </Fragment>
              )}
            </TableCell>
          </TableRow>
        </RootRef>
        {expanded &&
          <TableRow key={`${ID}-details`} className={classes.rowFullWidth}>
            <TableCell
              colSpan={variant === 'combined' ? 17 : 19}
              className={classes.cellFullWidth}
            >
              <div className={classNames(classes.details, {
                [classes.detailsArchive]: otherProps['Is Archive'],
              })}>
                <Table>
                  <TableBody>
                    {otherProps['Order Line'].map((item, index) => (
                      <OrdersTableRowItem
                        key={item.ID}
                        variant={variant}
                        expanded={expanded}
                        submitting={submitting}
                        change={change}
                        onTextFieldBlur={onTextFieldBlur}
                        onStockLineCreate={onStockLineCreate}
                        containsNarrativeCMNAMB={containsNarrativeCMNAMB}
                        Insurance={Insurance}
                        business={business}
                        Patient={Patient}
                        archive={otherProps['Is Archive']}
                        onHCPCCodeClick={(product) => {
                          this.onStockLineHistoryViewLinkClick(product);
                        }}
                        {...item}
                        {...{ 'Transmission Method': otherProps['Transmission Method'] }}
                        {...{ 'Primary Insurance': otherProps['Primary Insurance'] }}
                        {...{ 'Order ID': ID }}
                        {...index === 0
                          && !Patient['Exists Address'] ? {
                            editAddressHref: `/charts/${Patient['Record Number']}#contact-information`,
                          } : {}}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TableCell>
          </TableRow>
        }
      </Fragment>
    );
  }
}

OrdersTableRow.propTypes = {
  ID: PropTypes.number.isRequired,
  'Order Line': PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
    'Special State': PropTypes.oneOf(['current', 'future']),
  })).isRequired,
  Notes: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })),
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number,
    'First Name': PropTypes.string,
    'Last Name': PropTypes.stringd,
    'Date of Birth': PropTypes.string,
  }).isRequired,
  Diagnosis: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })),
  'Sales Representative': PropTypes.arrayOf(PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
  })).isRequired,
  Prescriber: PropTypes.shape({
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }).isRequired,
  Insurance: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })),
  Facility: PropTypes.shape({
    ID: PropTypes.number.isRequired,
  }),
  'Date of Creation': PropTypes.string.isRequired,
  'Transmission Method': PropTypes.string.isRequired,
  variant: PropTypes.oneOf([
    'workflow',
    'combined',
    'connected',
  ]).isRequired,
  actions: PropTypes.shape({
    bulkUpdateOrders: PropTypes.func.isRequired,
    fetchOrder: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  onTextFieldBlur: PropTypes.func,
  submitting: PropTypes.bool,
  change: PropTypes.func.isRequired,
  onStockLineCreate: PropTypes.func,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  notesTooltipPlacement: PropTypes.oneOf([
    'left',
    'top',
    'left-end',
  ]).isRequired,
  classes: PropTypes.shape({
    expansionPanelClickable: PropTypes.string.isRequired,
    cellExpansionActions: PropTypes.string.isRequired,
    cellExpansionActionsNotification: PropTypes.string.isRequired,
    cellSpecialState: PropTypes.string.isRequired,
    rowFullWidth: PropTypes.string.isRequired,
    cellFullWidth: PropTypes.string.isRequired,
    cellButton: PropTypes.string.isRequired,
    cellDateOfService: PropTypes.string.isRequired,
    cellHCPCSCode: PropTypes.string.isRequired,
    cellHCPCSCodeCollection: PropTypes.string.isRequired,
    cellTotalReimbs: PropTypes.string.isRequired,
    cellTotalCharge: PropTypes.string.isRequired,
    chip: PropTypes.string.isRequired,
    iconBlurb: PropTypes.string.isRequired,
    iconMoreVert: PropTypes.string.isRequired,
    iconArrowHeadBottom: PropTypes.string.isRequired,
    iconArrowHeadBottomExpanded: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    typographyNotes: PropTypes.string.isRequired,
    typographyOrderLinesCount: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    buttonNotesTooltipOpen: PropTypes.string.isRequired,
    noteTooltipPopper: PropTypes.string.isRequired,
    statusIcon: PropTypes.string.isRequired,
    statusIconActive: PropTypes.string.isRequired,
    statusIconInactive: PropTypes.string.isRequired,
    orderSelection: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersTableRow.defaultProps = {
  Notes: [],
  onTextFieldBlur: () => {},
  onStockLineCreate: () => {},
  submitting: false,
  Insurance: [],
  business: {},
  Diagnosis: null,
  Facility: null,
  archive: false,
  notesTooltipPlacement: 'left',
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ bulkUpdateOrders, fetchOrder, fetchArchiveOrder, reset, archiveOrder, unarchiveOrder, deleteArchiveOrder }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(withStyles(styles)(OrdersTableRow)),
);



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersTable/OrdersTableRow.js